import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Trans } from 'react-i18next';
import InfoIcon from '@mui/icons-material/Info';
import {
  AlertVariant,
  AlertStyleVariant,
  useAlert,
} from '@monorepo/shared/node_modules/@lwtears/lwt-common-frontend/lib/Alert';
import { tA11y } from '@monorepo/shared/node_modules/@lwtears/lwt-common-frontend/lib/@common/util/i18n-util';
import { useModal } from '@monorepo/shared/node_modules/@lwtears/lwt-common-frontend/lib/Modal';
import useAttemptStore from '@monorepo/shared/src/graph/state/query-hooks/use-attempt-store';
import { AttemptStatus } from '@monorepo/shared/src/graph/schema-generated-types-student';
import { AppRoutes } from '@monorepo/shared/src/types';
import useActivityAudio from '@monorepo/shared/src/hooks/use-activity-audio';
import UploadPage from 'components/common/UploadPage';
import PreviewSubmissionModal from 'components/common/Modals/PreviewSubmissionModal';
import CameraModal, { CameraModalStyles } from 'components/common/Modals/CameraModal';
import DeleteFileModal from 'components/common/Modals/DeleteFileModal';
import HavePrinterModal from 'components/common/Modals/HavePrinterModal';
import PrintPageModal from 'components/common/Modals/PrintPageModal';
import PreviewPhotoModal, {
  PreviewPhotoModalStyles,
} from 'components/common/Modals/PreviewPhotoModal';
import config from '@monorepo/shared/src/config';
import { Box, Button, Typography } from '@mui/material';

const useUploadPage = (book, assignmentActivityId, activityName) => {
  const [{ sounds, currentSound }, { playSoundFromState, loadAudioAssets, stopSound }] =
    useActivityAudio();

  const navigate = useNavigate();
  const location = useLocation();

  const query = new URLSearchParams(location.search);
  const bookCode = query.get('bookCode');
  const letter = bookCode?.slice(-1);

  const [{ isOpen }, { closeModal, openModal }] = useModal();
  const [, { openAlert, closeAlert }] = useAlert();
  const [currentStep, setCurrentStep] = useState<string>('');
  const [file, setFile] = useState<any>();
  const [audio, setAudio] = useState<any>();

  const [, { handleSaveAttempt }] = useAttemptStore();

  const formatFile = () => {
    return {
      description: `Assignment for letter ${letter} - ${activityName}`,
      file,
    };
  };

  const handleAttemptUpdate = async () => {
    const uploadFile = file && formatFile();
    return (
      assignmentActivityId &&
      (await handleSaveAttempt(
        {
          assignmentActivityId: JSON.parse(assignmentActivityId),
          status: AttemptStatus.COMPLETED,
          dateCompleted: new Date(),
        },
        uploadFile
      ))
    );
  };

  const goToNextStep = (newStep) => {
    return () => setCurrentStep(newStep);
  };

  const setFileAndStep = (file, nextStep) => {
    setFile(file);
    setCurrentStep(nextStep);
  };

  const onFileSubmit = async () => {
    try {
      await handleAttemptUpdate();
      closeModal();
      navigate(
        {
          pathname: `/${AppRoutes.ACTIVITY_COMPLETE}/${assignmentActivityId}`,
          search: location.search,
        },
        { state: { skipAttemptUpdate: true } }
      );
    } catch (error) {
      console.error('Error submitting file:', error);
      openAlert({
        body: (
          <Box marginTop={1}>
            <Typography variant="subtitle1">
              <Trans>{'Error submitting file. Please try again.'}</Trans>
            </Typography>
          </Box>
        ),
        variant: AlertVariant.ERROR,
        styleVariant: AlertStyleVariant.OUTLINED,
        options: {
          duration: 6000,
          disableClickAwayListener: true,
        },
        alertProps: {
          icon: <InfoIcon />,
        },
        action: (
          <Button
            onClick={() => {
              closeAlert();
            }}
          >
            {tA11y('buttons.close')}
          </Button>
        ),
      });
    }
  };

  const handleCloseModal = () => {
    setCurrentStep('');
    closeModal();
  };

  const steps = {
    havePrinter: {
      Modal: (
        <HavePrinterModal onAccept={goToNextStep('printerPreview')} onReject={handleCloseModal} />
      ),
      audio: 'can-you-use-a-printer.mp3',
    },
    printerPreview: {
      Modal: (
        <PrintPageModal book={book} afterPrint={handleCloseModal} onClose={handleCloseModal} />
      ),
      audio: 'tap-to-print-your-practice-page.mp3',
    },
    submitPage: {
      Modal: (
        <UploadPage
          onClickCamera={goToNextStep('camera')}
          onFileUpload={(file) => setFileAndStep(file, 'previewSubmission')}
          onClose={handleCloseModal}
        />
      ),
      audio: 'turn-in-your-work-by-photo-or-file.mp3',
    },
    camera: {
      Modal: (
        <CameraModal
          onPhotoTaken={(file) => setFileAndStep(file, 'previewModal')}
          onNoCameraPrivileges={goToNextStep('submitPage')}
        />
      ),
      ModalStyle: CameraModalStyles,
    },
    previewModal: {
      Modal: (
        <PreviewPhotoModal
          file={file}
          onAccept={goToNextStep('previewSubmission')}
          onReject={goToNextStep('camera')}
        />
      ),
      ModalStyle: PreviewPhotoModalStyles,
    },
    previewSubmission: {
      Modal: (
        <PreviewSubmissionModal
          file={file}
          onAccept={onFileSubmit}
          onReject={goToNextStep('deleteFile')}
        />
      ),
      PaperStyle: {
        width: 'max-content',
        maxHeight: '90vh',
        maxWidth: '100vw',
        overflow: 'auto',
      },
    },
    deleteFile: {
      Modal: (
        <DeleteFileModal
          onAccept={goToNextStep('submitPage')}
          onReject={goToNextStep('previewSubmission')}
        />
      ),
    },
  };

  useEffect(() => {
    const step = steps[currentStep];
    if (!step) return;
    if (step.audio) setAudio(step.audio);

    openModal({
      body: step.Modal,
      getContainerStyles: (defaultStyles) => ({
        ...defaultStyles,
        ...{ alignItems: 'center', justifyContent: 'center' },
      }),
      getPaperStyles: (defaultStyles) => ({
        ...defaultStyles,
        width: '100%',
        ...(step.PaperStyle ?? {}),
      }),
      ...(step.ModalStyle && { styles: step.ModalStyle }),
    });
  }, [currentStep]);

  useEffect(() => {
    if (audio && isOpen && sounds[audio]) {
      playSoundFromState(sounds[audio], true);
    }
  }, [audio, isOpen, sounds[audio]?.sound?._src]);

  useEffect(() => {
    const loadAudio = async (soundPath) => {
      const source = `${config.digitalAssetsUrl}/activities/en-us/${soundPath}`;
      currentSound && stopSound();
      await loadAudioAssets([
        {
          name: audio,
          category: audio,
          src: { src: source },
        },
      ]);
    };
    if (audio) {
      loadAudio(audio);
    }

    return () => currentSound && stopSound();
  }, [audio, isOpen]);

  return {
    setCurrentStep,
  };
};

export default useUploadPage;
